import { AtlasContentEditorProps, ContentEditor, Icon } from "@resource/atlas";
import { withConfig } from "@resource/atlas/content-editor/__utils/modules";
import {
  ValueSet,
  variablesModule,
} from "@resource/atlas/content-editor/variables";
import { unknownVariablesModule } from "@resource/atlas/content-editor/variables/__lib/module";
import { atlasPaperclip } from "@resource/atlas/icons";
import { useFlags } from "@resource/client-ffs";
import { CandidateTimezoneProvider } from "client/guide-content/rich-blocks/candidate-timezone-provider";
import { interviewsModule } from "client/guide-content/rich-blocks/interviews";
import { useAttachedFiles } from "client/guide-content/rich-blocks/interviews/__lib/attached-files-context";
import { useLinkCardModule } from "client/guide-content/rich-blocks/link-card";
import { useLinkCardsCollectionModule } from "client/guide-content/rich-blocks/link-cards-collection";
import {
  RichBlockContextProvider,
  RichBlockProviderOrganization,
} from "client/guide-content/rich-blocks/provider";
import { requestAvailabilityModule } from "client/guide-content/rich-blocks/request-availability";
import { shareAvailabilityModule } from "client/guide-content/rich-blocks/share-availability";
import { useGuideIdContext } from "client/utils/guide-id-provider";
import { Interview } from "components/EditInterviewModal/EditInterviewModalView";
import FeatureFlags from "generated/FeatureFlags";
import { map, omit, uniqBy } from "lodash";
import { useMemo } from "react";
import {
  guideUpdateSpecSet,
  GuideUpdateVariableSet,
} from "shared/guide-content/update";

import { ErrorBoundary } from "../ErrorBoundary";
import { useBaseModules } from "./base-modules";

type GuideUpdateEditorProps = AtlasContentEditorProps & {
  valueSet: ValueSet<GuideUpdateVariableSet>;
  /** @default "placeholder" */
  undefinedMode?: "placeholder" | "error";
  onInterviewEdit?: (interview: Interview) => unknown;
};

function AttachedFiles({
  files,
}: {
  files: { id: string; filename: string }[];
}) {
  return files.length > 0 ? (
    <div className="space-y-2">
      {map(files, ({ id, filename }) => (
        <div
          key={id}
          className="shadow-1 rounded bg-light-gray-200 flex p-3 items-center gap-2"
        >
          <Icon content={atlasPaperclip} className="text-subtle" />
          <p className="truncate text-body-md-heavy text-dark">{filename}</p>
        </div>
      ))}
    </div>
  ) : null;
}

function GuideUpdateEditorWithRequiredContext({
  valueSet,
  undefinedMode,
  modules = [],
  trailingContentSlot,
  onInterviewEdit,
  ...props
}: GuideUpdateEditorProps) {
  const {
    [FeatureFlags.CODESIGNAL_INTEGRATION]: codesignalIntegrationEnabled,
  } = useFlags();
  const { attachedFiles } = useAttachedFiles();
  const uniqueAttachedFiles = uniqBy(attachedFiles, "id");

  const specSet = useMemo(() => {
    if (codesignalIntegrationEnabled) return guideUpdateSpecSet;

    return omit(guideUpdateSpecSet, "codesignalTestAssessmentLink");
  }, [codesignalIntegrationEnabled]);

  return (
    <ErrorBoundary>
      <ContentEditor
        {...props}
        trailingContentSlot={
          trailingContentSlot || uniqueAttachedFiles.length > 0 ? (
            <div className="space-y-4">
              {trailingContentSlot}
              <AttachedFiles files={uniqueAttachedFiles} />
            </div>
          ) : undefined
        }
        modules={[
          ...useBaseModules(),
          withConfig(variablesModule, {
            specSet,
            valueSet,
            undefinedMode,
          }),
          unknownVariablesModule,
          useLinkCardModule(),
          useLinkCardsCollectionModule(),
          shareAvailabilityModule,
          withConfig(interviewsModule, { onEdit: onInterviewEdit }),
          ...modules,
        ]}
      />
    </ErrorBoundary>
  );
}

export function GuideUpdateEditor({
  guidePostId,
  candidateTimezone,
  sendToEmail,
  modules = [],
  organization,
  ...props
}: GuideUpdateEditorProps & {
  guidePostId?: string;
  candidateTimezone?: string | null;
  sendToEmail?: string;
  organization?: RichBlockProviderOrganization;
}) {
  const guideIdContext = useGuideIdContext();
  const { [FeatureFlags.REQUEST_AVAILABILITY]: requestAvailabilityEnabled } =
    useFlags();

  const optionalModules = [];
  if (requestAvailabilityEnabled)
    optionalModules.push(requestAvailabilityModule);

  return (
    <RichBlockContextProvider
      guidePostId={guidePostId}
      guideId={guideIdContext?.guideId}
      sendToEmail={sendToEmail}
      organization={organization}
    >
      <CandidateTimezoneProvider timezone={candidateTimezone ?? null}>
        <GuideUpdateEditorWithRequiredContext
          {...props}
          modules={[...modules, ...optionalModules]}
        />
      </CandidateTimezoneProvider>
    </RichBlockContextProvider>
  );
}

export function GuideUpdateTemplateEditor({
  organization,
  modules = [],
  ...props
}: GuideUpdateEditorProps & {
  organization?: RichBlockProviderOrganization | null;
}) {
  const { [FeatureFlags.REQUEST_AVAILABILITY]: requestAvailabilityEnabled } =
    useFlags();

  const optionalModules = [];
  if (requestAvailabilityEnabled)
    optionalModules.push(requestAvailabilityModule);

  return (
    <RichBlockContextProvider organization={organization}>
      <GuideUpdateEditorWithRequiredContext
        {...props}
        modules={[...modules, ...optionalModules]}
      />
    </RichBlockContextProvider>
  );
}
